<template>
  <div class="group-a-details">
    <a-table
      :columns="columns"
      :data-source="tableData"
      :loading="loading"
      :pagination="paginationConfig"
      :scroll="{ x: 1400 }"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, record, index }">
        <template v-if="column.key === 'no'">
          <span>{{ getRowNumber(index) }}</span>
        </template>
        <template v-else-if="column.key === 'name'">
          <a-tooltip :title="record.name" placement="topLeft">
            <span class="name-text name-ellipsis">{{ record.name }}</span>
          </a-tooltip>
        </template>
        <template v-else-if="column.key === 'phoneNumber'">
          <span>{{ formatPhoneNumber(record.phoneNumber) }}</span>
        </template>
        <template v-else-if="column.key === 'surgeryDate'">
          <span>{{ formatDate(record.surgeryDate) }}</span>
        </template>
        <template v-else-if="column.key === 'shareCount'">
          <span :class="['share-count', { 'share-count-highlight': record.shareCount > 0 }]">
            {{ record.shareCount || 0 }}
          </span>
        </template>
        <template v-else-if="column.key === 'satisfactionScore'">
          <span v-if="record.satisfactionScore !== null" class="satisfaction-score">
            {{ record.satisfactionScore }}
          </span>
          <span v-else class="no-data">-</span>
        </template>
        <template v-else-if="column.key === 'reviewContent'">
          <a-tooltip v-if="record.reviewContent" :title="record.reviewContent" placement="topLeft">
            <div class="review-content">
              {{ record.reviewContent }}
            </div>
          </a-tooltip>
          <span v-else class="no-data">-</span>
        </template>
      </template>

      <template #summary v-if="statsData">
        <a-table-summary fixed>
          <a-table-summary-row class="summary-row">
            <a-table-summary-cell :index="0" :col-span="6" align="center">
              <strong>합계/평균</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="6" align="center">
              <strong class="summary-value">{{ formatNumber(statsData.totalShares) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="7" align="center">
              <strong class="summary-value">{{ formatAvgScore(statsData.avgSatisfactionScore) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="8" :col-span="1">
              <!-- 후기 내용 컬럼은 빈칸 -->
            </a-table-summary-cell>
          </a-table-summary-row>
        </a-table-summary>
      </template>
    </a-table>
  </div>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { Table, Tooltip } from 'ant-design-vue';
import dayjs from 'dayjs';
import reportApi from '../api';
import { useStore } from 'vuex';

const { Summary } = Table;

export default {
  name: 'GroupADetails',
  components: {
    ATable: Table,
    ATooltip: Tooltip,
    ATableSummary: Summary,
    ATableSummaryRow: Summary.Row,
    ATableSummaryCell: Summary.Cell,
  },
  props: {
    reportId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const instance = getCurrentInstance();
    const api = reportApi(instance.proxy.$apiInstance, store);

    const loading = ref(false);
    const tableData = ref([]);
    const statsData = ref(null);
    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0
    });
    const sortBy = ref('surgeryDate');
    const sortOrder = ref('desc');

    // 테이블 컬럼 정의 (이미지의 컬럼명 순서대로)
    const columns = [
      {
        title: 'No.',
        key: 'no',
        width: 70,
        align: 'center',
        fixed: 'left',
      },
      {
        title: '이름',
        dataIndex: 'name',
        key: 'name',
        width: 120,
        align: 'center',
        ellipsis: true,
      },
      {
        title: '전화번호',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 130,
        align: 'center',
      },
      {
        title: '병원명',
        dataIndex: 'hospitalName',
        key: 'hospitalName',
        width: 180,
      },
      {
        title: '수술명',
        dataIndex: 'surgeryType',
        key: 'surgeryType',
        width: 120,
        align: 'center',
      },
      {
        title: '수술일자',
        dataIndex: 'surgeryDate',
        key: 'surgeryDate',
        width: 120,
        align: 'center',
      },
      {
        title: '공유 횟수',
        dataIndex: 'shareCount',
        key: 'shareCount',
        width: 100,
        align: 'center',
      },
      {
        title: '만족도 점수',
        dataIndex: 'satisfactionScore',
        key: 'satisfactionScore',
        width: 110,
        align: 'center',
      },
      {
        title: '후기 내용',
        dataIndex: 'reviewContent',
        key: 'reviewContent',
        minWidth: 300,
        ellipsis: true,
      },
    ];

    // 페이지네이션 설정
    const paginationConfig = computed(() => ({
      current: pagination.value.current,
      pageSize: pagination.value.pageSize,
      total: pagination.value.total,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      showTotal: (total) => `전체 ${total.toLocaleString()}건`,
    }));

    // 행 번호 계산
    const getRowNumber = (index) => {
      return (pagination.value.current - 1) * pagination.value.pageSize + index + 1;
    };

    // 날짜 포맷팅
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD');
    };

    // 전화번호 포맷팅
    const formatPhoneNumber = (phoneNumber) => {
      if (!phoneNumber) return '-';
      // 010-1234-5678 형식으로 변환
      const cleaned = phoneNumber.replace(/\D/g, '');
      if (cleaned.length === 11) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
      }
      return phoneNumber;
    };

    // 숫자 포맷팅 (천단위 콤마)
    const formatNumber = (num) => {
      if (num === null || num === undefined) return '-';
      return num.toLocaleString();
    };

    // 평균 점수 포맷팅 (소수점 2자리)
    const formatAvgScore = (score) => {
      if (score === null || score === undefined) return '-';
      return score.toFixed(2);
    };

    // 데이터 조회
    const fetchData = async () => {
      if (!props.reportId) {
        console.log('🚫 [GroupADetails] reportId가 없습니다:', props.reportId);
        return;
      }

      console.log('🔍 [GroupADetails] API 호출 시작 - reportId:', props.reportId);
      loading.value = true;
      try {
        await api.getGroupDetails(
          {
            reportId: props.reportId,
            group: 'A',
            page: pagination.value.current,
            limit: pagination.value.pageSize,
            sortBy: sortBy.value,
            sortOrder: sortOrder.value
          },
          (response) => {
            console.log('✅ [GroupADetails] API 응답 성공:', response);
            console.log('✅ [GroupADetails] response.data 타입:', Array.isArray(response.data) ? 'Array' : typeof response.data);

            // api.js에서 response.data를 전달하므로, response.data가 직접 배열
            if (response.data && Array.isArray(response.data)) {
              tableData.value = response.data;
              console.log('📊 [GroupADetails] 테이블 데이터 설정:', tableData.value.length, '건');

              if (response.pagination) {
                pagination.value.current = parseInt(response.pagination.page) || 1;
                pagination.value.pageSize = parseInt(response.pagination.limit) || 10;
                pagination.value.total = response.pagination.total || 0;
                pagination.value.totalPages = response.pagination.totalPages || 0;
                console.log('📄 [GroupADetails] 페이지네이션:', pagination.value);
              }

              // stats 데이터 저장
              if (response.stats && response.stats.overall && response.stats.overall.length > 0) {
                statsData.value = response.stats.overall[0];
                console.log('📈 [GroupADetails] 통계 데이터:', statsData.value);
              }
            }
          },
          (error) => {
            console.error('❌ [GroupADetails] API 호출 실패:', error);
            console.error('❌ [GroupADetails] 에러 상세:', error.response?.data);
          }
        );
      } finally {
        loading.value = false;
      }
    };

    // 테이블 변경 핸들러 (페이지네이션)
    const handleTableChange = (paginationParams) => {
      // 페이지네이션 업데이트
      if (paginationParams) {
        pagination.value.current = paginationParams.current;
        pagination.value.pageSize = paginationParams.pageSize;
      }

      // 데이터 재조회
      fetchData();
    };

    // reportId 변경 감지
    watch(() => props.reportId, (newReportId, oldReportId) => {
      console.log('🔄 [GroupADetails] reportId 변경 감지:', {
        old: oldReportId,
        new: newReportId
      });
      if (newReportId) {
        pagination.value.current = 1; // 페이지 초기화
        fetchData();
      }
    }, { immediate: true });

    return {
      columns,
      tableData,
      statsData,
      loading,
      paginationConfig,
      getRowNumber,
      formatDate,
      formatPhoneNumber,
      formatNumber,
      formatAvgScore,
      handleTableChange,
    };
  },
};
</script>

<style scoped>
.group-a-details {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}

:deep(.ant-table-wrapper) {
  background: #fff;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
  border-bottom: 2px solid #e8e8e8;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}

.name-text {
  font-weight: 500;
  color: #262626;
}

.name-ellipsis {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}

.share-count {
  color: #1890ff;
  font-weight: 500;
}

.share-count-highlight {
  color: #f5222d !important;
  font-weight: 700 !important;
  background: #fff1f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.satisfaction-score {
  color: #52c41a;
  font-weight: 600;
}

.review-content {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #595959;
  font-size: 13px;
  cursor: help;
}

.no-data {
  color: #bfbfbf;
}

/* 페이지네이션 스타일 */
:deep(.ant-pagination) {
  margin-top: 24px;
  text-align: center;
}

:deep(.ant-pagination-total-text) {
  font-weight: 500;
  color: #262626;
}

/* 합계/평균 행 스타일 */
:deep(.summary-row) {
  background: #e6f7ff !important;
  border-top: 2px solid #1890ff;
}

:deep(.summary-row td) {
  font-weight: 600 !important;
  color: #0050b3 !important;
  padding: 12px 8px !important;
}

.summary-value {
  font-size: 14px;
  color: #0050b3;
}
</style>
